import { create } from 'zustand'
import { CreativeService } from '../../../openapi/services/CreativeService'

export interface GenerationStatus {
  status: 'generating' | 'ready' | 'failed'
  imageUrl?: string
  name?: string
  type?: string
  error?: string
}

const AUTO_CLEAR_MS = 5 * 60 * 1000 // 5 min — clear terminal statuses to prevent unbounded growth

interface GenerationStatusStore {
  statuses: Record<string, GenerationStatus>
  setStatus: (creativeId: string, status: GenerationStatus) => void
  getStatus: (creativeId: string) => GenerationStatus | undefined
  clearStatus: (creativeId: string) => void
}

export const useGenerationStatusStore = create<GenerationStatusStore>((set, get) => ({
  statuses: {},
  setStatus: (creativeId, status) => {
    set(state => ({
      statuses: { ...state.statuses, [creativeId]: status },
    }))
    if (status.status === 'ready' || status.status === 'failed') {
      setTimeout(() => {
        const current = get().statuses[creativeId]
        if (current && current.status !== 'generating') {
          set(state => {
            const { [creativeId]: _, ...rest } = state.statuses
            return { statuses: rest }
          })
        }
      }, AUTO_CLEAR_MS)
    }
  },
  getStatus: creativeId => get().statuses[creativeId],
  clearStatus: creativeId =>
    set(state => {
      const { [creativeId]: _, ...rest } = state.statuses
      return { statuses: rest }
    }),
}))

/**
 * Fetch the authoritative generation status from the server and push it into
 * the store if terminal. Single entry point used by every reconciliation
 * trigger (Pusher event, on-mount, Pusher reconnect) so the DB is the only
 * source of truth — the Pusher payload is treated as a notification, not data.
 *
 * Legacy fallback: creatives created before the generationStatus column was
 * tracked carry `generationStatus = null`. For those, mirror the historical
 * client behaviour — if the data blob has any of the canonical content keys,
 * treat the creative as ready. This keeps old cards from flipping to a stuck
 * "generating" state after deploy. New creatives never hit this branch because
 * their status is written by markGenerationStarted/Finished.
 *
 * Returns a cleanup function that cancels the pending fetch result.
 */
export function reconcileCreativeStatus(creativeId: string): () => void {
  let cancelled = false
  CreativeService.creativeControllerFindOneV1(creativeId)
    .then(result => {
      if (cancelled) return
      const entity = result?.creative
      if (!entity) return

      const data = entity.data as Record<string, any> | undefined
      const content = (data?.content ?? undefined) as Record<string, any> | undefined
      const generationError = entity.generationError
      const imageUrl = content?.imageUrl || data?.imageUrl

      let resolvedStatus: 'ready' | 'failed' | undefined
      if (entity.generationStatus === 'ready' || entity.generationStatus === 'failed') {
        resolvedStatus = entity.generationStatus
      } else if (entity.generationStatus == null && hasLegacyReadyContent(content)) {
        resolvedStatus = 'ready'
      }
      if (!resolvedStatus) return

      useGenerationStatusStore.getState().setStatus(creativeId, {
        status: resolvedStatus,
        imageUrl,
        name: entity.name,
        type: entity.type,
        error:
          resolvedStatus === 'failed' && typeof generationError === 'string'
            ? generationError
            : undefined,
      })
    })
    .catch(err => console.error('Failed to reconcile creative generation status:', err))
  return () => {
    cancelled = true
  }
}

function hasLegacyReadyContent(content: Record<string, any> | undefined): boolean {
  if (!content) return false
  return Boolean(content.caption || content.message || content.subject || content.headline)
}
